// src/components/ProjectBuilder/TaskKpiStatsBanner.tsx
import { Box, Paper, Typography, LinearProgress, Grid } from '@mui/material';

export interface TaskKpiStatsBannerProps {
  kpiStats: {
    aggregateKpiScore: number;
    totalActions: number;
    completedActions: number;
    kpiByOrganization: {
      Fraport?: { kpiScore: number; actionCount: number; completedActionCount: number };
      Tenant?: { kpiScore: number; actionCount: number; completedActionCount: number };
      Client?: { kpiScore: number; actionCount: number; completedActionCount: number };
    };
  };
}

export default function TaskKpiStatsBanner({ kpiStats }: TaskKpiStatsBannerProps) {
  const { aggregateKpiScore, totalActions, completedActions, kpiByOrganization } = kpiStats;
  
  // Format KPI score with sign and status
  const formatKpiScore = (score: number): { display: string; color: string; status: string } => {
    if (score < 0) {
      return {
        display: `${score} days`,
        color: '#2e7d32', // Green
        status: 'Ahead',
      };
    } else if (score > 0) {
      return {
        display: `+${score} days`,
        color: '#d32f2f', // Red
        status: 'Behind',
      };
    } else {
      return {
        display: '0 days',
        color: '#757575', // Gray
        status: 'On Time',
      };
    }
  };

  const kpiDisplay = formatKpiScore(aggregateKpiScore);
  const completionPercentage = totalActions > 0 ? (completedActions / totalActions) * 100 : 0;

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
      {/* Aggregate KPI Score */}
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold', color: kpiDisplay.color, mb: 0.5 }}>
          {kpiDisplay.display}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {kpiDisplay.status}
        </Typography>
      </Box>

      {/* Organization Breakdown */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {kpiByOrganization.Fraport && (
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Fraport
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {formatKpiScore(kpiByOrganization.Fraport.kpiScore).display}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({kpiByOrganization.Fraport.completedActionCount}/{kpiByOrganization.Fraport.actionCount})
              </Typography>
            </Box>
          </Grid>
        )}
        {kpiByOrganization.Tenant && (
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Tenant
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {formatKpiScore(kpiByOrganization.Tenant.kpiScore).display}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({kpiByOrganization.Tenant.completedActionCount}/{kpiByOrganization.Tenant.actionCount})
              </Typography>
            </Box>
          </Grid>
        )}
        {kpiByOrganization.Client && (
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Client
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {formatKpiScore(kpiByOrganization.Client.kpiScore).display}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({kpiByOrganization.Client.completedActionCount}/{kpiByOrganization.Client.actionCount})
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Action Completion Progress */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Actions: {completedActions}/{totalActions} completed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(completionPercentage)}%
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={completionPercentage} 
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>
    </Paper>
  );
}

